import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Target, 
  Clock, 
  Shield, 
  Car, 
  Phone, 
  MapPin,
  CheckCircle,
  Star,
  Users,
  Mountain,
  ArrowRight,
  Compass
} from "lucide-react";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import campBullisHeroImage from "@/assets/camp-bullis-hero.avif";

const CampBullisTransportation = () => {
  const faqs = [
    {
      question: "Can Rick's Black Car Service enter Camp Bullis?",
      answer: "Yes! Rick's Black Car Service has verified base access credentials (DBIDS) that allow our chauffeurs to enter Camp Bullis without a sponsor. We can drop you off and pick you up directly at your designated location on the installation."
    },
    {
      question: "What is Camp Bullis used for?",
      answer: "Camp Bullis is a U.S. Army training installation used primarily for field training exercises, combat medic training, and various military courses. It serves as a critical training ground for soldiers from Fort Sam Houston and other installations."
    },
    {
      question: "How far is Camp Bullis from San Antonio International Airport?",
      answer: "Camp Bullis is approximately 19 miles northwest of SAT Airport, located in the Texas Hill Country north of San Antonio. Travel time is typically 20-25 minutes depending on traffic conditions."
    },
    {
      question: "What information do you need for base entry?",
      answer: "We require the full legal name and date of birth for all passengers at booking. All passengers over 16 must have a valid government-issued photo ID (CAC, Driver's License, or Passport) ready to present at the gate."
    },
    {
      question: "Do you provide transportation for training exercises?",
      answer: "Absolutely! We provide reliable transportation for personnel attending training exercises, courses, and field operations at Camp Bullis. Our chauffeurs understand the unique scheduling needs of military training."
    },
    {
      question: "Can you accommodate groups for training courses?",
      answer: "Yes! Our Audi Q7 luxury SUV seats up to 6 passengers. For larger groups attending training courses, we can coordinate multiple vehicles with synchronized arrivals and departures."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Camp Bullis Transportation Service",
    "description": "Professional luxury transportation to Camp Bullis, a U.S. Army training installation in the Texas Hill Country. Military field training, combat medic courses, and personnel transportation. Verified base access, DBIDS-credentialed chauffeurs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "Camp Bullis, San Antonio, TX"
    },
    "serviceType": "Military Base Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ricksblackcarservicesantonio.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Military Transportation",
        "item": "https://ricksblackcarservicesantonio.com/jbsa-military-transportation"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Camp Bullis Transportation",
        "item": "https://ricksblackcarservicesantonio.com/camp-bullis-transportation"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Camp Bullis Transportation | Military Training Installation</title>
        <meta 
          name="description" 
          content="Professional Camp Bullis transportation. Verified base access for military training installation. DBIDS-credentialed chauffeurs. Book today!" 
        />
        <meta name="keywords" content="Camp Bullis transportation, Camp Bullis car service, military training transportation San Antonio, JBSA Camp Bullis, Fort Sam Houston training" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/camp-bullis-transportation" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${campBullisHeroImage})`,
              backgroundPosition: 'center'
            }}
          />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <Breadcrumb 
              items={[
                { name: "Services", url: "/services" },
                { name: "Military Transportation", url: "/services/military-transportation" },
                { name: "Camp Bullis", url: "/camp-bullis-transportation" }
              ]} 
            />
            <div className="flex justify-center mb-6">
              <div className="bg-primary/20 p-4 rounded-full">
                <Target className="h-16 w-16 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Camp Bullis Transportation
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Reliable transportation to San Antonio's premier military training installation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <Link to="/request-quote">Book Training Transport</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-background/10 border-white text-white hover:bg-white hover:text-foreground">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Shield className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Verified Base Access</span>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">DBIDS Credentialed</span>
              </div>
              <div className="flex flex-col items-center">
                <Car className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Luxury Audi Fleet</span>
              </div>
              <div className="flex flex-col items-center">
                <Star className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">5-Star Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* About Camp Bullis */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">About Camp Bullis</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                  Camp Bullis is a 28,000-acre U.S. Army training installation located in the Texas Hill Country, approximately 19 miles northwest of downtown San Antonio. It serves as a critical training ground for military personnel from across the region.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Target className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Combat Training</h3>
                    <p className="text-sm text-muted-foreground">
                      Field training exercises and combat readiness courses
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Users className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Medic Training</h3>
                    <p className="text-sm text-muted-foreground">
                      Combat medic and healthcare specialist courses
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Mountain className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Hill Country Terrain</h3>
                    <p className="text-sm text-muted-foreground">
                      Diverse landscape ideal for realistic field operations
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="text-center">
                <Link to="/blog/what-is-jbsa-access-guide" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                  Learn About JBSA Access Requirements
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Who We Serve at Camp Bullis</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional transportation for military personnel and training support
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Training Personnel</h3>
                  <p className="text-sm text-muted-foreground">
                    Soldiers attending field training exercises and courses
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Users className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Combat Medics</h3>
                  <p className="text-sm text-muted-foreground">
                    Healthcare specialists attending medical training programs
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Compass className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Course Instructors</h3>
                  <p className="text-sm text-muted-foreground">
                    Training cadre and military instructors
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Support Staff</h3>
                  <p className="text-sm text-muted-foreground">
                    Contractors and civilian personnel with base access
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Transportation Packages */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Camp Bullis Transportation Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Flat-rate pricing with gratuity included. No hidden fees or surprises.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Airport Package */}
              <Card className="relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-semibold">
                  MOST POPULAR
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    SAT Airport
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$167</span>
                    <p className="text-sm text-muted-foreground">to Camp Bullis</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flight monitoring
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Meet & greet service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/request-quote">Book Airport Transfer</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Downtown Package */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Downtown/Riverwalk
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$156</span>
                    <p className="text-sm text-muted-foreground">to Camp Bullis</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Hotel pickup available
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flexible timing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/request-quote">Book Downtown Transfer</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Hourly Package */}
              <Card className="border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Training Hourly
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$100</span>
                    <span className="text-muted-foreground">/hour</span>
                    <p className="text-sm text-muted-foreground">3-hour minimum</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Dedicated chauffeur
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Multiple stops included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Wait time included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/request-quote">Book Hourly Service</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Pricing Disclaimer */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50 max-w-5xl mx-auto">
              <p className="text-sm text-muted-foreground text-center">
                <span className="font-medium text-foreground">Note:</span> Fares shown are estimates and calculated dynamically based on distance and time. 
                Actual fare may vary slightly depending on your exact pickup location. Use our{' '}
                <Link to="/request-quote" className="text-primary hover:underline font-medium">Instant Quote Calculator</Link>{' '}
                for a precise quote.
              </p>
            </div>
          </div>
        </section>

        {/* Training Support */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Training Exercise Support</h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  We understand the unique scheduling demands of military training. Our flexible service adapts to your training timeline.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-primary/20">
                  <CardHeader className="bg-primary/5">
                    <CardTitle className="flex items-center gap-3">
                      <Clock className="h-6 w-6 text-primary" />
                      Early Morning Departures
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <p className="text-muted-foreground">
                      Training often starts before dawn. We provide reliable early morning transportation to ensure you arrive on time for formation and training exercises.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        0400 and 0500 pickups available
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        On-time guarantee
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Professional, punctual service
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-primary/20">
                  <CardHeader className="bg-primary/5">
                    <CardTitle className="flex items-center gap-3">
                      <Target className="h-6 w-6 text-primary" />
                      Flexible Scheduling
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <p className="text-muted-foreground">
                      Field training schedules can change. We offer flexible booking modifications and understand that training timelines may shift.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Schedule change accommodation
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Communication via call or text
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        24/7 availability
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Camp Bullis Transportation FAQs</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Common questions about transportation to Camp Bullis
              </p>
            </div>
            <ServiceFAQ faqs={faqs} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Book Your Camp Bullis Transportation?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience reliable, professional transportation with verified JBSA base access. Book online or call us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <Link to="/request-quote">Book Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default CampBullisTransportation;
